import React from 'react';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });
    
    // Log error for monitoring
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // In production, you might want to send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          retryCount={this.state.retryCount}
          {...this.props}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, errorInfo, onRetry, retryCount, showDetails = false }) => {
  const navigate = useNavigate();
  
  const isNetworkError = error?.message?.includes('fetch') || 
                        error?.message?.includes('network') ||
                        error?.message?.includes('timeout');
  
  const isRateLimitError = error?.message?.includes('Request blocked') ||
                          error?.message?.includes('Rate limit');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        p: 3,
        textAlign: 'center'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%'
        }}
      >
        <Typography variant="h4" gutterBottom color="error">
          Oops! Something went wrong
        </Typography>
        
        {isRateLimitError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Too many requests detected. Please wait a moment before trying again.
          </Alert>
        )}
        
        {isNetworkError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Network connection issue. Please check your internet connection.
          </Alert>
        )}
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {isRateLimitError 
            ? "We've detected unusual activity. Please wait a few minutes before continuing."
            : isNetworkError 
            ? "Unable to connect to the server. Please check your connection and try again."
            : "An unexpected error occurred. Our team has been notified."}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {!isRateLimitError && retryCount < 3 && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
              sx={{ minWidth: 120 }}
            >
              Try Again
            </Button>
          )}
          
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{ minWidth: 120 }}
          >
            Go Home
          </Button>
        </Box>
        
        {showDetails && error && (
          <Box sx={{ mt: 3, textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>
              Error Details:
            </Typography>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflow: 'auto',
                maxHeight: 200
              }}
            >
              <Typography component="pre">
                {error.toString()}
                {errorInfo && errorInfo.componentStack}
              </Typography>
            </Paper>
          </Box>
        )}
        
        {retryCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Retry attempts: {retryCount}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default ErrorBoundary;